import { isPlatformServer } from "@angular/common";
import { ChangeDetectorRef, Component, Inject, Input, OnDestroy, OnInit, PLATFORM_ID, ViewEncapsulation } from "@angular/core";
import { Loader } from "@googlemaps/js-api-loader";
import MarkerClusterer from "@googlemaps/markerclustererplus";
import { ThemeService } from "../../services/theme.service";
import { Subscription } from "rxjs";
import { AssetPipe } from "../../pipes/asset.pipe";
import { ComponentRendering } from "@sitecore-jss/sitecore-jss-angular";
import { JssStylingService } from "../../jss-styling.service";

declare var google: any;

interface Branch {
    Merchant: string;
    BranchName: string;
    Address: string;
    AddressNumber: string;
    Country: string;
    Prefecture: string;
    City: string;
    ZipCode: string;
    Longitude: string;
    Latitude: string;
}

@Component({
    selector: "app-yellow-branch-map",
    templateUrl: "./yellow-branch-map.component.html",
    styleUrls: ["./yellow-branch-map.component.scss"],
    encapsulation: ViewEncapsulation.None,
})
export class YellowBranchMapComponent implements OnInit, OnDestroy {
    @Input() rendering: ComponentRendering;
    @Input() branches: Branch[] = [];
    @Input() partner: string;
    isBranchPage: any;
    infoWindoData: any;
    private energySaveSubscription: Subscription;
    isVisible: boolean = false;
    openMarker = null as any;
    toBeClosedMarker = null as any;
    private userMarker: any;
    allMarkers: any;
    energySave: boolean = false;
    locatorBranchMarker: string;
    clusterUrl: string;
    map: any;
    clusters: any;
    _isPlatformServer: boolean = false;
    dynamicClass: string;
    dynamicId: string;

    constructor(@Inject(PLATFORM_ID) public readonly platformId: string, public jssStylingService: JssStylingService, private themeService: ThemeService, private cdr: ChangeDetectorRef) {
        platformId;
    }

    ngOnInit() {
        // remove this after implementation is done
        console.log("yellowBranchMap component initialized with component data", this.rendering);

        if ((this.rendering?.fields?.result as any)?.length > 0) {
            this.branches = [...(this.rendering?.fields?.result as any)];
        }
        this.dynamicClass = this.jssStylingService.getComponentClass(this.rendering);
        this.dynamicId = this.jssStylingService.getComponentId(this.rendering);
        this._isPlatformServer = isPlatformServer(this.platformId);
        if (!this._isPlatformServer) {
            this.energySaveSubscription = this.themeService.isEnegySaveModeEnabled().subscribe((energySave) => {
                this.energySave = energySave;

                this.locatorBranchMarker = this.energySave ? new AssetPipe().transform("images/map/yellow-marker-energy") : new AssetPipe().transform("images/map/yellow-marker");
                this.clusterUrl = this.energySave ? new AssetPipe().transform("images/map/cluster-energy") : new AssetPipe().transform("images/map/cluster");

                this.loadGoogleMaps().then(() => {
                    this.initMap();
                    this.addMarkers();
                    this.getUserLocation();
                });
            });
        }
    }

    ngOnDestroy(): void {
        if (!this._isPlatformServer && this.energySaveSubscription) {
            this.energySaveSubscription.unsubscribe();
        }
    }

    loadGoogleMaps(): Promise<unknown> {
        const loader = new Loader({
            apiKey: "AIzaSyBEjbaXg3R4j6PVehVdi3kvsUQy0kRpWVU",
            version: "weekly",
            libraries: ["geometry"],
        });

        return loader.load();
    }

    initMap(): void {
        this.map = new google.maps.Map(document.getElementById("map"), {
            center: { lat: 38.0395, lng: 23.8074 },
            zoom: 12,
            streetViewControl: false,
            styles: this.getCustomMapStyle(),
            clickableIcons: false,
            fullscreenControl: false,
        });
    }

    addMarkers(): void {
        const markersBranches = this.branches.map((branch: Branch) => {
            const marker = this.createMarker({ lat: Number(branch.Latitude), lng: Number(branch.Longitude) }, branch.BranchName, this.locatorBranchMarker + ".svg", this.locatorBranchMarker + "-selected.svg");

            if (this.branches.length >= 1 && !this.isBranchPage) {
                this.addMarkerClickListener(marker, branch);
            }

            return marker;
        });

        markersBranches.forEach((marker: any) => marker.setMap(this.map));

        this.allMarkers = [...markersBranches];

        this.clusters = new MarkerClusterer(this.map, this.allMarkers, {
            imagePath: this.clusterUrl,
            gridSize: 60,
            maxZoom: 15,
        });

        this.clusters.setClusterClass("pb-cluster");

        if (this.allMarkers.length === 1) {
            this.map.setCenter({ lat: Number(this.branches[0]?.Latitude), lng: Number(this.branches[0]?.Longitude) });
            this.map.setZoom(15);
        }
    }

    createMarker(location: any, title: any, iconUrl: string, selectedIconUrl: string): any {
        return new google.maps.Marker({
            position: location,
            name: title,
            icon: {
                url: iconUrl,
                scaledSize: new google.maps.Size(45, 56),
            },
            selectedIcon: {
                url: selectedIconUrl,
                scaledSize: new google.maps.Size(70, 80),
            },
            defaultIcon: {
                url: iconUrl,
                scaledSize: new google.maps.Size(45, 56),
            },
        });
    }

    addMarkerClickListener(marker: any, data: any): void {
        marker.addListener("click", () => {
            this.openPopup(marker, data);
        });
    }

    openPopup(marker: any, data: any): void {
        if (this.toBeClosedMarker != null) {
            this.openMarker.setIcon(this.openMarker.defaultIcon);
        }

        if (marker === this.openMarker && this.isVisible) {
            this.isVisible = false;
            this.openMarker.setIcon(this.openMarker.defaultIcon);
        } else {
            this.isVisible = true;
            this.infoWindoData = data;
            this.openMarker = marker;
            marker.setIcon(marker.selectedIcon);
            this.toBeClosedMarker = marker;
            this.map.panTo(marker.getPosition());
            this.map.setZoom(18);
        }

        this.cdr.detectChanges();
    }

    onClosePopup(e: boolean) {
        this.openMarker.setIcon(this.openMarker.defaultIcon);
        this.isVisible = e;
        this.cdr.detectChanges();
    }

    getUserLocation(): void {
        if (typeof window !== "undefined" && navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                (position: GeolocationPosition) => {
                    const userLocation = {
                        lat: position.coords.latitude,
                        lng: position.coords.longitude,
                    };
                    this.map.setCenter(userLocation);

                    this.userMarker = new google.maps.Marker({
                        position: userLocation,
                        title: "Your Location",
                        icon: {
                            url: "https://maps.google.com/mapfiles/ms/icons/blue-dot.png",
                        },
                        map: this.map,
                    });
                    if (this.userMarker) {
                        // console.log("success");
                    }

                    const nearestMarker = this.findNearestMarker(userLocation);
                    if (nearestMarker) {
                        this.map.setCenter(nearestMarker.getPosition());
                        this.map.setZoom(15);
                    } else {
                        this.map.setCenter(userLocation);
                    }
                },
                (error: GeolocationPositionError) => {
                    if (error) {
                        // console.log("Error getting user location:", error.message);
                    }
                    if (this.allMarkers?.length > 1) {
                        this.map.setCenter({ lat: 37.9755464, lng: 23.7353597 });
                        this.map.setZoom(7);
                    }
                }
            );
        } else {
            console.log("Geolocation is not supported by this browser.");
        }
    }

    removeMarkers() {
        if (this.allMarkers && this.allMarkers.length > 0) {
            this.allMarkers.forEach((marker: any) => {
                marker.setMap(null);
            });
            this.allMarkers = [];
            this.clusters.clearMarkers();
        }
    }

    findNearestMarker(userLocation: any): any {
        let nearestMarker = null;
        let shortestDistance = Infinity;

        this.allMarkers.forEach((marker: any) => {
            const markerPosition = marker.getPosition();
            const distance = google.maps.geometry.spherical.computeDistanceBetween(new google.maps.LatLng(userLocation.lat, userLocation.lng), markerPosition);

            if (distance < shortestDistance) {
                shortestDistance = distance;
                nearestMarker = marker;
            }
        });

        return nearestMarker;
    }

    findMarkerByBranch(branch: any): any {
        return this.allMarkers.find((marker: any) => marker.code === branch.Code);
    }

    getCustomMapStyle(): any {
        const customMapStyle = [
            { featureType: "administrative", elementType: "labels.text.fill", stylers: [{ color: "#444444" }] },
            {
                featureType: "administrative.province",
                elementType: "labels.text.fill",
                stylers: [
                    { color: "#002f30" },
                    {
                        visibility: "on",
                    },
                ],
            },
            { featureType: "administrative.locality", elementType: "labels.text.fill", stylers: [{ color: "#002f30" }] },
            {
                featureType: "administrative.neighborhood",
                elementType: "labels.text.fill",
                stylers: [
                    { visibility: "on" },
                    {
                        color: "#002f30",
                    },
                ],
            },
            { featureType: "landscape", elementType: "all", stylers: [{ color: "#f2f2f2" }] },
            { featureType: "landscape", elementType: "labels", stylers: [{ visibility: "simplified" }] },
            { featureType: "landscape", elementType: "labels.text.fill", stylers: [{ color: "#1e1e1e" }] },
            {
                featureType: "landscape.man_made",
                elementType: "geometry.fill",
                stylers: [
                    { saturation: "0" },
                    {
                        color: "#edebe4",
                    },
                ],
            },
            {
                featureType: "landscape.natural.landcover",
                elementType: "geometry.fill",
                stylers: [
                    { color: "#edebe4" },
                    {
                        visibility: "on",
                    },
                ],
            },
            { featureType: "poi", elementType: "geometry.fill", stylers: [{ visibility: "on" }] },
            {
                featureType: "poi.attraction",
                elementType: "all",
                stylers: [
                    { visibility: "simplified" },
                    {
                        color: "#808080",
                    },
                ],
            },
            { featureType: "poi.business", elementType: "all", stylers: [{ visibility: "off" }] },
            { featureType: "poi.medical", elementType: "all", stylers: [{ visibility: "off" }] },
            { featureType: "poi.park", elementType: "all", stylers: [{ color: "#e5e1d7" }] },
            {
                featureType: "poi.park",
                elementType: "labels.text.fill",
                stylers: [
                    { color: "#8c7e7e" },
                    {
                        visibility: "on",
                    },
                ],
            },
            { featureType: "poi.park", elementType: "labels.text.stroke", stylers: [{ visibility: "off" }] },
            { featureType: "poi.school", elementType: "all", stylers: [{ visibility: "off" }] },
            { featureType: "poi.sports_complex", elementType: "all", stylers: [{ visibility: "off" }] },
            {
                featureType: "road",
                elementType: "all",
                stylers: [
                    { saturation: -100 },
                    {
                        lightness: 45,
                    },
                ],
            },
            {
                featureType: "road",
                elementType: "geometry",
                stylers: [
                    { color: "#fafaf7" },
                    {
                        visibility: "simplified",
                    },
                ],
            },
            { featureType: "road.highway", elementType: "geometry.fill", stylers: [{ color: "#ffffff" }] },
            {
                featureType: "road.highway",
                elementType: "geometry.stroke",
                stylers: [
                    {
                        color: "#ffffff",
                    },
                ],
            },
            {
                featureType: "road.arterial",
                elementType: "all",
                stylers: [
                    {
                        visibility: "simplified",
                    },
                ],
            },
            {
                featureType: "road.arterial",
                elementType: "labels.icon",
                stylers: [
                    {
                        visibility: "off",
                    },
                ],
            },
            {
                featureType: "road.local",
                elementType: "all",
                stylers: [
                    {
                        visibility: "on",
                    },
                ],
            },
            {
                featureType: "road.local",
                elementType: "geometry.fill",
                stylers: [
                    {
                        visibility: "off",
                    },
                ],
            },
            {
                featureType: "road.local",
                elementType: "labels.text.fill",
                stylers: [
                    {
                        color: "#878275",
                    },
                ],
            },
            {
                featureType: "road.local",
                elementType: "labels.text.stroke",
                stylers: [
                    {
                        visibility: "off",
                    },
                ],
            },
            {
                featureType: "transit",
                elementType: "geometry.fill",
                stylers: [
                    {
                        color: "#ffffff",
                    },
                ],
            },
            {
                featureType: "transit.station.airport",
                elementType: "all",
                stylers: [
                    {
                        visibility: "on",
                    },
                    {
                        color: "#d3cdbc",
                    },
                ],
            },
            {
                featureType: "transit.station.airport",
                elementType: "labels.text",
                stylers: [
                    {
                        visibility: "simplified",
                    },
                ],
            },
            {
                featureType: "transit.station.airport",
                elementType: "labels.text.fill",
                stylers: [
                    {
                        color: "#002f30",
                    },
                ],
            },
            {
                featureType: "transit.station.bus",
                elementType: "all",
                stylers: [
                    {
                        visibility: "off",
                    },
                ],
            },
            {
                featureType: "water",
                elementType: "all",
                stylers: [
                    {
                        color: "#46bcec",
                    },
                    {
                        visibility: "on",
                    },
                ],
            },
            {
                featureType: "water",
                elementType: "geometry.fill",
                stylers: [
                    {
                        saturation: "-10",
                    },
                    {
                        lightness: "-77",
                    },
                    {
                        gamma: "3.60",
                    },
                    {
                        color: "#5a9dff",
                    },
                ],
            },
            {
                featureType: "water",
                elementType: "labels.text.fill",
                stylers: [
                    {
                        color: "#ffffff",
                    },
                ],
            },
        ];
        const customMapStyleEnergy = [
            {
                featureType: "all",
                elementType: "labels.text.fill",
                stylers: [
                    {
                        saturation: 36,
                    },
                    {
                        color: "#cac3af",
                    },
                    {
                        lightness: 40,
                    },
                ],
            },
            {
                featureType: "all",
                elementType: "labels.text.stroke",
                stylers: [
                    {
                        visibility: "on",
                    },
                    {
                        color: "#000000",
                    },
                    {
                        lightness: 16,
                    },
                ],
            },
            {
                featureType: "all",
                elementType: "labels.icon",
                stylers: [
                    {
                        visibility: "off",
                    },
                    {
                        color: "#cac3af",
                    },
                ],
            },
            {
                featureType: "administrative",
                elementType: "geometry.fill",
                stylers: [
                    {
                        color: "#f70000",
                    },
                    {
                        lightness: 20,
                    },
                    {
                        visibility: "off",
                    },
                ],
            },
            {
                featureType: "administrative",
                elementType: "geometry.stroke",
                stylers: [
                    {
                        color: "#aaa509",
                    },
                    {
                        lightness: 17,
                    },
                    {
                        weight: 1.2,
                    },
                ],
            },
            {
                featureType: "landscape",
                elementType: "geometry",
                stylers: [
                    {
                        lightness: 20,
                    },
                    {
                        visibility: "simplified",
                    },
                    {
                        color: "#001f20",
                    },
                ],
            },
            {
                featureType: "poi",
                elementType: "geometry",
                stylers: [
                    {
                        lightness: 21,
                    },
                    {
                        visibility: "simplified",
                    },
                    {
                        color: "#43413a",
                    },
                ],
            },
            {
                featureType: "poi.medical",
                elementType: "geometry.fill",
                stylers: [
                    {
                        visibility: "on",
                    },
                    {
                        color: "#5b5b5b",
                    },
                ],
            },
            {
                featureType: "poi.park",
                elementType: "geometry.fill",
                stylers: [
                    {
                        visibility: "on",
                    },
                    {
                        color: "#557475",
                    },
                ],
            },
            {
                featureType: "road",
                elementType: "geometry.fill",
                stylers: [
                    {
                        gamma: "1",
                    },
                ],
            },
            {
                featureType: "road",
                elementType: "labels.text.fill",
                stylers: [
                    {
                        visibility: "on",
                    },
                    {
                        color: "#cac3af",
                    },
                ],
            },
            {
                featureType: "road.highway",
                elementType: "geometry.fill",
                stylers: [
                    {
                        color: "#505555",
                    },
                    {
                        lightness: 17,
                    },
                ],
            },
            {
                featureType: "road.highway",
                elementType: "geometry.stroke",
                stylers: [
                    {
                        color: "#000000",
                    },
                    {
                        lightness: 29,
                    },
                    {
                        weight: 0.2,
                    },
                    {
                        visibility: "off",
                    },
                ],
            },
            {
                featureType: "road.arterial",
                elementType: "geometry",
                stylers: [
                    {
                        lightness: 18,
                    },
                    {
                        visibility: "on",
                    },
                    {
                        color: "#2a5253",
                    },
                ],
            },
            {
                featureType: "road.arterial",
                elementType: "geometry.fill",
                stylers: [
                    {
                        visibility: "on",
                    },
                    {
                        color: "#2a5253",
                    },
                ],
            },
            {
                featureType: "road.arterial",
                elementType: "geometry.stroke",
                stylers: [
                    {
                        visibility: "on",
                    },
                    {
                        color: "#2a5253",
                    },
                ],
            },
            {
                featureType: "road.local",
                elementType: "geometry",
                stylers: [
                    {
                        lightness: 16,
                    },
                    {
                        weight: "1",
                    },
                ],
            },
            {
                featureType: "road.local",
                elementType: "geometry.fill",
                stylers: [
                    {
                        visibility: "on",
                    },
                    {
                        color: "#2a5253",
                    },
                    {
                        weight: "1",
                    },
                ],
            },
            {
                featureType: "road.local",
                elementType: "geometry.stroke",
                stylers: [
                    {
                        visibility: "off",
                    },
                    {
                        color: "#2a5253",
                    },
                    {
                        weight: "0.3",
                    },
                ],
            },
            {
                featureType: "transit",
                elementType: "geometry",
                stylers: [
                    {
                        color: "#424242",
                    },
                    {
                        lightness: 19,
                    },
                ],
            },
            {
                featureType: "transit.station.airport",
                elementType: "geometry.fill",
                stylers: [
                    {
                        visibility: "simplified",
                    },
                    {
                        color: "#a0a0a0",
                    },
                ],
            },
            {
                featureType: "water",
                elementType: "geometry",
                stylers: [
                    {
                        color: "#08162b",
                    },
                    {
                        lightness: 17,
                    },
                ],
            },
        ];

        return this.energySave ? customMapStyleEnergy : customMapStyle;
    }
}
